import React, { useCallback, useMemo, useState } from "react";
import "./style.css";
import { useSelector } from "react-redux";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import {
  Button,
  DatePicker,
  Segmented,
  Select,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import NewMiniDrawer from "../../../components/navMenu/menu-nav";
import acompanhamentoApi from "../../../service/modules/acompanhamento.api";
import useOpcoesDeFiltro from "../opcoes-de-filtro";
import {
  COLUNAS_DA_FROTA,
  COLUNAS_DA_LINHA_DO_TEMPO,
  COLUNAS_DO_MENSAL,
  agruparMensal,
  consolidarFrota,
  etapasDaLinhaDoTempo,
  formatarDuracao,
  prepararLinhas,
} from "../../../util/relatorios/agrupar";
import { baixarCSV, baixarPlanilha } from "../../../util/relatorios/exportar";
import { baixarPDF } from "../../../util/relatorios/pdf";

const { RangePicker } = DatePicker;

/**
 * Teto de páginas de uma varredura. Mil fretes é mais do que qualquer período que alguém consolida
 * de uma vez, e o que passar disso aparece escrito na tela e no arquivo — consolidado truncado em
 * silêncio é pior que consolidado nenhum.
 */
const MAX_PAGINAS = 20;
const POR_PAGINA = 50;

const RELATORIOS = [
  { value: "frota", label: "Visão consolidada de frota" },
  { value: "mensal", label: "Mensal por placa e motorista" },
  { value: "linha-do-tempo", label: "Linha do tempo do frete" },
];

/**
 * Cor e abreviação do selo da lista de fretes, que é onde o operador aprendeu a ler cada status.
 * Copiadas do `renderStatusColor` de `frete-list-copy/components/data-frete-table-action.js` — mudou
 * lá, muda aqui. O nome inteiro fica na dica e no arquivo exportado.
 */
const SELO_DO_STATUS = {
  "Pendente de validação": { texto: "P. Validação", cor: "#A855F7" },
  "Pendente de contratação": { texto: "P.Contratação", cor: "#F97316" },
  Contratado: { cor: "#059669" },
  "Em trânsito": { cor: "#16A34A" },
  Entregue: { cor: "#2563EB" },
  Finalizado: { cor: "#6B7280" },
  Cancelado: { cor: "#B91C1C" },
  "Deslocando-se para a coleta": { cor: "#22C55E" },
  "Aguardando Carregamento": { cor: "#F59E0B" },
  "Aguardando Documentação": { cor: "#FBBF24" },
  "Aguardando Descarregamento": { cor: "#EAB308" },
  "Criado, não iniciado": { cor: "#64748B" },
  "Etapa de carregamento finalizada": { cor: "#0D9488" },
  "Etapa de descarregamento finalizada": { cor: "#0284C7" },
};

const FILTROS_VAZIOS = {
  periodo: null,
  uidShipper: undefined,
  driverUid: undefined,
  plate: "",
};

function dataBR(iso) {
  if (!iso) return null;
  const [ano, mes, dia] = String(iso).split("-");
  return dia ? `${dia}/${mes}/${ano}` : iso;
}

/** Coluna de minuto na tela é lida, não somada — por isso "3h30" aqui e inteiro no arquivo. */
function paraTabela(colunas) {
  return colunas.map((coluna) => ({
    key: coluna.chave,
    dataIndex: coluna.chave,
    title: coluna.rotulo.replace(" (min)", ""),
    render:
      coluna.tipo === "minutos"
        ? (valor) => formatarDuracao(valor)
        : (valor) =>
            valor === null || valor === undefined || valor === "" ? "—" : valor,
  }));
}

function Relatorios() {
  const user = useSelector((state) => state.user);
  const logado = useSelector((state) => state.usuarioLogado);

  // Os tres cards da tela de acompanhamento abrem aqui ja no relatorio que o operador clicou.
  const [parametrosDaUrl] = useSearchParams();
  const tipoDaUrl = parametrosDaUrl.get("tipo");

  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [relatorio, setRelatorio] = useState(
    RELATORIOS.some((r) => r.value === tipoDaUrl) ? tipoDaUrl : "frota",
  );

  const [linhas, setLinhas] = useState([]);
  const [dataDeCorte, setDataDeCorte] = useState(null);
  const [paginasLidas, setPaginasLidas] = useState(0);
  const [faltouPagina, setFaltouPagina] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [gerado, setGerado] = useState(false);
  const [erro, setErro] = useState(null);

  const escopo = useMemo(() => {
    const afiliados = Array.isArray(user?.uidShipperAffiliates)
      ? user.uidShipperAffiliates
      : [];
    const proprio = user?.uidShipper ? [user.uidShipper] : [];
    const uids = Array.from(new Set([...afiliados, ...proprio]));

    return {
      viewALL: Boolean(user?.viewALL),
      uids,
      params: user?.viewALL
        ? { idUser: user?.uid, viewAll: "true" }
        : { uidShipper: uids },
    };
  }, [user]);

  const { embarcadores, motoristas, placas } = useOpcoesDeFiltro(escopo);

  const periodo = useMemo(
    () => ({
      de: filtros.periodo?.[0]?.format("YYYY-MM-DD"),
      ate: filtros.periodo?.[1]?.format("YYYY-MM-DD"),
    }),
    [filtros.periodo],
  );

  const gerar = useCallback(
    async (tipo = relatorio) => {
      if (!escopo.viewALL && !escopo.uids.length) return;

      setCarregando(true);
      setErro(null);

      try {
        const acumulado = [];
        let cursor;
        let paginas = 0;
        let sobrou = false;
        let corte = null;

        // Página a página, como o mapa faz com o trajeto. A regra de escopo proíbe carregar tudo numa
        // requisição; consolidar exige o período inteiro. Pedir em sequência atende as duas coisas.
        do {
          // eslint-disable-next-line no-await-in-loop
          const resposta = await acompanhamentoApi.listarRelatorio({
            ...escopo.params,
            ...(filtros.uidShipper
              ? {
                  uidShipper: filtros.uidShipper,
                  idUser: undefined,
                  viewAll: undefined,
                }
              : {}),
            limit: POR_PAGINA,
            cursor,
            driverUid: filtros.driverUid,
            plate: filtros.plate || undefined,
            dataDe: periodo.de,
            dataAte: periodo.ate,
            // A varredura já mede as etapas para somar os tempos, mas só as devolve quando pedidas:
            // traduzir a coordenada de cada uma em município é o custo, e as outras guias não usam.
            incluirEtapas: tipo === "linha-do-tempo" ? "true" : undefined,
          });

          acumulado.push(...(resposta?.data?.data ?? []));
          corte = resposta?.data?.dataDeCorte ?? null;
          cursor = resposta?.data?.pagination?.nextCursor;
          paginas += 1;
          sobrou = Boolean(resposta?.data?.pagination?.hasMore);
        } while (cursor && sobrou && paginas < MAX_PAGINAS);

        setLinhas(prepararLinhas(acumulado));
        setDataDeCorte(corte);
        setPaginasLidas(paginas);
        setFaltouPagina(sobrou && paginas >= MAX_PAGINAS);
        setGerado(true);
      } catch (e) {
        const doBackend = e?.response?.data?.message;
        setErro(doBackend || "Não foi possível gerar o relatório.");
        setLinhas([]);
        setGerado(false);
        console.error("Erro ao gerar o relatório:", e);
      } finally {
        setCarregando(false);
      }
    },
    [escopo, filtros, periodo, relatorio],
  );

  /** Trocar de guia depois de gerar mostraria a linha do tempo vazia: as etapas não vieram. */
  const trocarRelatorio = (tipo) => {
    setRelatorio(tipo);
    if (
      gerado &&
      tipo === "linha-do-tempo" &&
      !linhas.some((l) => l.linhaDoTempo)
    ) {
      gerar(tipo);
    }
  };

  const consolidado = useMemo(() => consolidarFrota(linhas), [linhas]);
  const mensal = useMemo(() => agruparMensal(linhas), [linhas]);
  const etapasDaTabela = useMemo(() => etapasDaLinhaDoTempo(linhas), [linhas]);

  const escolhido = RELATORIOS.find((r) => r.value === relatorio);

  /** O que responde a pergunta meses depois: recorte, corte e o que ficou fora. */
  const cabecalho = useMemo(() => {
    const partes = [
      periodo.de || periodo.ate
        ? `Período: ${dataBR(periodo.de) || "início"} a ${dataBR(periodo.ate) || "hoje"}`
        : "Período: todo o histórico disponível",
      `Fretes no consolidado: ${consolidado.fretes}`,
      `Com histórico de status: ${consolidado.medidos}`,
      dataDeCorte
        ? `Histórico com hora do servidor a partir de ${dataBR(dataDeCorte)}`
        : "Data de corte do histórico ainda não configurada",
    ];

    if (consolidado.semRegistro) {
      partes.push(
        `${consolidado.semRegistro} frete(s) depois do corte sem nenhum evento registrado`,
      );
    }
    if (faltouPagina) {
      partes.push(
        `Atenção: o período tem mais fretes do que as ${MAX_PAGINAS} páginas lidas. Estreite o período.`,
      );
    }

    return partes;
  }, [periodo, consolidado, dataDeCorte, faltouPagina]);

  const paraExportar = useMemo(() => {
    if (relatorio === "linha-do-tempo") {
      return {
        titulo: "Linha do tempo do frete",
        cabecalho,
        secoes: [
          {
            nome: "Etapas",
            colunas: COLUNAS_DA_LINHA_DO_TEMPO,
            linhas: etapasDaTabela,
          },
        ],
      };
    }

    if (relatorio === "mensal") {
      return {
        titulo: "Relatório mensal por placa e motorista",
        cabecalho,
        secoes: [
          { nome: "Mensal", colunas: COLUNAS_DO_MENSAL, linhas: mensal },
        ],
      };
    }

    return {
      titulo: "Visão consolidada de frota",
      cabecalho,
      secoes: [{ nome: "Fretes", colunas: COLUNAS_DA_FROTA, linhas }],
    };
  }, [relatorio, cabecalho, linhas, mensal, etapasDaTabela]);

  const exportar = (formato) => {
    try {
      if (formato === "csv") baixarCSV(paraExportar);
      else if (formato === "xlsx") baixarPlanilha(paraExportar);
      else baixarPDF(paraExportar);
    } catch (e) {
      message.error("Não foi possível gerar o arquivo.");
      console.error("Erro ao exportar o relatório:", e);
    }
  };

  const vazio = gerado && !linhas.length;
  const semHistorico = gerado && linhas.length && !consolidado.medidos;
  const semEtapas = linhas.filter((l) => !l.etapas).length;
  const podeExportar =
    gerado &&
    (relatorio === "linha-do-tempo" ? etapasDaTabela.length : linhas.length);

  const conteudo = (
    <div className="rel9">
      <header className="rel9__cabecalho">
        <Link className="rel9__voltar" to="/freightsDetail">
          ‹ Acompanhamento de entregas
        </Link>
        <h1>Relatórios</h1>
        <p>
          Tempo em movimento, tempo parado e tempo operacional, medidos pelo
          histórico de status com hora do servidor.
        </p>
      </header>

      <section className="rel9__filtros">
        <div className="rel9__filtro">
          <label>Período de coleta</label>
          <RangePicker
            format="DD/MM/YYYY"
            value={filtros.periodo}
            onChange={(periodo) => setFiltros((f) => ({ ...f, periodo }))}
            allowEmpty={[true, true]}
          />
        </div>
        <div className="rel9__filtro">
          <label>Embarcador</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Todos"
            options={embarcadores}
            value={filtros.uidShipper}
            onChange={(uidShipper) => setFiltros((f) => ({ ...f, uidShipper }))}
          />
        </div>
        <div className="rel9__filtro">
          <label>Motorista</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Todos"
            options={motoristas}
            value={filtros.driverUid}
            onChange={(driverUid) => setFiltros((f) => ({ ...f, driverUid }))}
          />
        </div>
        <div className="rel9__filtro">
          <label>Placa</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Todas"
            options={placas}
            value={filtros.plate || undefined}
            onChange={(v) => setFiltros((f) => ({ ...f, plate: v ?? "" }))}
          />
        </div>
        <div className="rel9__filtro-acoes">
          <Button onClick={() => setFiltros(FILTROS_VAZIOS)}>Limpar</Button>
          <Button type="primary" loading={carregando} onClick={() => gerar()}>
            Gerar relatório
          </Button>
        </div>
      </section>

      <section className="rel9__corpo">
        <div className="rel9__barra">
          <Segmented
            options={RELATORIOS}
            value={relatorio}
            onChange={trocarRelatorio}
          />
          <div className="rel9__exportar">
            <Button
              icon={<DownloadOutlined />}
              disabled={!podeExportar}
              onClick={() => exportar("csv")}
            >
              CSV
            </Button>
            <Button
              icon={<DownloadOutlined />}
              disabled={!podeExportar}
              onClick={() => exportar("xlsx")}
            >
              Excel
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              disabled={!podeExportar}
              onClick={() => exportar("pdf")}
            >
              PDF
            </Button>
          </div>
        </div>

        {erro && <p className="rel9__erro">{erro}</p>}

        {!gerado && !carregando && !erro && (
          <p className="rel9__nota">
            Escolha o período e clique em <strong>Gerar relatório</strong>.
          </p>
        )}

        {vazio && (
          <p className="rel9__nota">
            Nenhum frete no período e no escopo escolhidos.
          </p>
        )}

        {gerado && !vazio && (
          <>
            <ul className="rel9__contexto">
              {cabecalho.map((linha) => (
                <li key={linha}>{linha}</li>
              ))}
            </ul>

            {semHistorico ? (
              <p className="rel9__nota rel9__nota--atencao">
                Os {consolidado.fretes} fretes do período não têm histórico de
                status registrado com hora do servidor, então não há tempo a
                medir. Isso muda quando o aplicativo novo estiver publicado — o
                histórico anterior não é recuperável, porque nunca foi gravado
                com hora confiável.
              </p>
            ) : null}

            {relatorio === "frota" && (
              <Table
                size="small"
                rowKey="id"
                loading={carregando}
                columns={[
                  ...paraTabela(COLUNAS_DA_FROTA).slice(0, -1),
                  {
                    key: "statusAtual",
                    dataIndex: "statusAtual",
                    title: "Status",
                    render: (valor) => {
                      if (!valor) return "—";
                      const selo = SELO_DO_STATUS[valor] || {};
                      return (
                        <Tooltip title={valor}>
                          <Tag color={selo.cor || "#020202"}>
                            {selo.texto || valor}
                          </Tag>
                        </Tooltip>
                      );
                    },
                  },
                ]}
                dataSource={linhas}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                scroll={{ x: "max-content" }}
              />
            )}

            {relatorio === "mensal" && (
              <Table
                size="small"
                rowKey={(l) => `${l.mesReferencia}|${l.motorista}|${l.placa}`}
                columns={paraTabela(COLUNAS_DO_MENSAL)}
                dataSource={mensal}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                scroll={{ x: "max-content" }}
              />
            )}

            {relatorio === "linha-do-tempo" && (
              <>
                <Table
                  size="small"
                  rowKey="chave"
                  loading={carregando}
                  columns={paraTabela(COLUNAS_DA_LINHA_DO_TEMPO)}
                  dataSource={etapasDaTabela}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  scroll={{ x: "max-content" }}
                />
                {semEtapas > 0 && (
                  <p className="rel9__nota">
                    {semEtapas} dos {consolidado.fretes} fretes do período não
                    têm etapa registrada com hora do servidor e por isso não
                    aparecem na tabela.
                  </p>
                )}
              </>
            )}
          </>
        )}
      </section>

      {gerado && (
        <p className="rel9__rodape">
          {escolhido?.label} · lido em {paginasLidas} página(s) de {POR_PAGINA}{" "}
          fretes
        </p>
      )}
    </div>
  );

  return logado > 0 ? (
    <NewMiniDrawer divOpen={conteudo} />
  ) : (
    <Navigate to="/login" />
  );
}

export default Relatorios;
